import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getClosureMsg,
  createOrUpdateClosureMsg,
  deleteClosureMsg,
  updateRepeatOccureneceClosureMsg,
  getClosureMsgMediaFile,
  getResetClosureMsg,
} from "../services/closureMsg";

interface ClosureMsgApiResponse {
  data: {
    data: Record<string, unknown>[];
  };
}

interface ClosureMsgState {
  closureMsgStatus: string;
  closureMsgMediaFileStatus: string;
  closureMsgDeleteStatus: string;
  closureMsgUpdateRepeatOccrStatus: string;
  isCMPending: boolean;
  isCMSave: boolean;
  isCMDelete: boolean;
  isCMError: boolean;
  closureOriginalData: Record<string, unknown>[];
  closureMsgData: Record<string, unknown>[];
  closureMsgMediaFileInfo: unknown | null;
}

const initialState: ClosureMsgState = {
  closureMsgStatus: "idle",
  closureMsgMediaFileStatus: "idle",
  closureMsgDeleteStatus: "idle",
  closureMsgUpdateRepeatOccrStatus: "idle",
  isCMPending: false,
  isCMSave: false,
  isCMDelete: false,
  isCMError: false,
  closureOriginalData: [],
  closureMsgData: [],
  closureMsgMediaFileInfo: null,
};

const normalizeMessages = (
  data: Record<string, unknown>[],
): Record<string, unknown>[] => {
  return data.reduce<Record<string, unknown>[]>((acc, cur) => {
    if (cur.type === "Repeat" && !!cur.occurrences) {
      const occurrencesObj = cur.occurrences as Record<
        string,
        Record<string, unknown>
      >;
      const occurencesArray = Object.keys(occurrencesObj).map((key) => {
        return occurrencesObj[key];
      });

      acc = [
        ...acc, // Anti-pattern [bad-performance]
        ...occurencesArray.map((item) => ({
          ...cur,
          occurrenceId: item.id,
          startDate: item.startDate,
          startTime: item.startTime,
          endDate: item.endDate,
          endTime: item.endTime,
          startTimeZone: item.startTimeZone,
          endTimeZone: item.endTimeZone,
          status: item.status,
          ...(Object.keys(item).length > 0 &&
            Object.keys(item)
              .filter((a) => a.includes("message"))
              .reduce<Record<string, unknown>>((msgAcc, msgCur) => {
                msgAcc[msgCur] = item[msgCur];
                return msgAcc;
              }, {})),
        })),
      ];
    } else {
      acc.push({
        ...cur,
      });
    }

    return acc;
  }, []);
};

const processPayload = (
  payload: Record<string, unknown>,
): Record<string, unknown> => {
  const clonedPayload = { ...payload };
  delete clonedPayload["__typename"];
  delete clonedPayload["__operation"];
  return clonedPayload;
};

const processMessages = (processedPayload: Record<string, unknown>) => {
  try {
    const raw = processedPayload.messages;
    const parsedMessages =
      typeof raw === "string"
        ? (JSON.parse(raw) as Record<string, unknown> | null)
        : (raw as Record<string, unknown> | null);
    return {
      message_en: parsedMessages?.en ?? null,
      message_fr: parsedMessages?.fr ?? null,
      message_sp: parsedMessages?.sp ?? null,
      message_zh: parsedMessages?.zh ?? null,
      message_zhTW: parsedMessages?.["zh-TW"] ?? null,
    };
  } catch {
    return {
      message_en: null,
      message_fr: null,
      message_sp: null,
      message_zh: null,
      message_zhTW: null,
    };
  }
};

const updateClosureMsgData = (
  state: ClosureMsgState,
  processedPayload: Record<string, unknown>,
) => {
  const newOccurrenceClosureMsgData = normalizeMessages([processedPayload]);
  const excludedClosureData = state.closureMsgData.filter(
    (item) => item.id !== processedPayload.id,
  );

  state.closureMsgData = [
    ...excludedClosureData,
    ...newOccurrenceClosureMsgData,
  ];

  state.closureMsgData = state.closureMsgData.map((item) => {
    if (item.id === processedPayload.id) {
      return {
        ...item,
        highLight: true,
        ...processMessages(processedPayload),
      };
    } else {
      return {
        ...item,
        highLight: false,
      };
    }
  });
};

/**
 * Delete operation of a closure message in the store
 * @param  state                          current state
 * @param  incomingMessage               received payload representing a series of messages
 */
const deleteClosureMessage = (
  state: ClosureMsgState,
  incomingMessage: Record<string, unknown>,
) => {
  // Filter out the series of message to alter
  const otherMessages = state.closureMsgData.filter(
    (message) => message.id !== incomingMessage.id,
  );

  /**
   * Number of messages of the repeat pattern being altered by a DELETE op
   * @type {Number}
   */
  const countDiff = state.closureMsgData.length - otherMessages.length;

  if (
    incomingMessage.type === "Repeat" &&
    countDiff > 0 && // Whether any message was matched by the filter
    incomingMessage.occurrences != null &&
    countDiff !==
      Object.keys(incomingMessage.occurrences as Record<string, unknown>).length // Whether an occurrence was deleted
  ) {
    const normalizedMessages = normalizeMessages([
      { ...incomingMessage, ...processMessages(incomingMessage) },
    ]);

    state.closureMsgData = otherMessages.concat(normalizedMessages);
  } else {
    // Delete the entire series OR leave the collection prestine
    state.closureMsgData = otherMessages;
  }
};

const closureMsgSlice = createSlice({
  name: "closureMsg",
  initialState,
  reducers: {
    createCMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      const processedPayload = processPayload(payload);
      const messages = processMessages(processedPayload);

      state.closureOriginalData.push(processedPayload);
      const tmpOccurrenceClosureMsgData = normalizeMessages([processedPayload]);

      const occurrenceClosureMsgData = tmpOccurrenceClosureMsgData.map(
        (item) => ({
          ...item,
          highLight: true,
          ...messages,
        }),
      );

      state.closureMsgData = [
        ...state.closureMsgData,
        ...occurrenceClosureMsgData,
      ];
    },

    updateCMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      const processedPayload = processPayload(payload);
      updateClosureMsgData(state, processedPayload);
    },

    deleteCMData: (
      state,
      { payload }: { payload: Record<string, unknown> },
    ) => {
      const processedPayload = processPayload(payload);
      deleteClosureMessage(state, processedPayload);
    },

    resetHighlight: (
      state,
      { payload }: { payload: Record<string, unknown>[] },
    ) => {
      state.closureMsgData = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClosureMsg.pending, (state) => {
        state.closureMsgStatus = "pending";
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = false;
        state.closureMsgData = [];
      })
      .addCase(getClosureMsg.fulfilled, (state, action) => {
        const payload = action.payload as ClosureMsgApiResponse;
        state.closureMsgStatus = "success";
        state.isCMPending = false;
        state.closureOriginalData = payload.data.data;
        state.closureMsgData = normalizeMessages(payload.data.data);
      })
      .addCase(getClosureMsg.rejected, (state) => {
        state.closureMsgStatus = "rejected";
        state.isCMPending = false;
        state.isCMError = true;
        state.closureMsgData = [];
      })
      .addCase(getClosureMsgMediaFile.pending, (state) => {
        state.closureMsgMediaFileStatus = "pending";
        state.closureMsgMediaFileInfo = null;
      })
      .addCase(getClosureMsgMediaFile.fulfilled, (state, action) => {
        const { payload } = action;
        state.closureMsgMediaFileStatus = "success";
        state.closureMsgMediaFileInfo = payload;
      })
      .addCase(getClosureMsgMediaFile.rejected, (state) => {
        state.closureMsgMediaFileStatus = "rejected";
        state.closureMsgMediaFileInfo = null;
      })
      .addCase(createOrUpdateClosureMsg.pending, (state) => {
        state.isCMPending = true;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = false;
      })
      .addCase(createOrUpdateClosureMsg.fulfilled, (state, action) => {
        const payload = action.payload as ClosureMsgApiResponse;
        state.closureMsgStatus = "created";
        state.closureOriginalData = payload.data.data;
        state.closureMsgData = normalizeMessages(payload.data.data);
        state.isCMPending = false;
        state.isCMSave = true;
        state.isCMDelete = false;
        state.isCMError = false;
      })
      .addCase(createOrUpdateClosureMsg.rejected, (state) => {
        state.closureMsgStatus = "rejected";
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = true;
      })
      .addCase(deleteClosureMsg.pending, (state) => {
        state.isCMPending = true;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = false;
        state.closureMsgDeleteStatus = "pending";
      })
      .addCase(deleteClosureMsg.fulfilled, (state, action) => {
        const payload = action.payload as ClosureMsgApiResponse;
        state.closureMsgDeleteStatus = "deleted";
        state.closureOriginalData = payload.data.data;
        state.closureMsgData = normalizeMessages(payload.data.data);
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = true;
        state.isCMError = false;
      })
      .addCase(deleteClosureMsg.rejected, (state) => {
        state.closureMsgDeleteStatus = "rejected";
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = true;
      })
      .addCase(updateRepeatOccureneceClosureMsg.pending, (state) => {
        state.closureMsgUpdateRepeatOccrStatus = "pending";
        state.isCMPending = true;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = false;
      })
      .addCase(updateRepeatOccureneceClosureMsg.fulfilled, (state, action) => {
        const payload = action.payload as ClosureMsgApiResponse;
        state.closureMsgUpdateRepeatOccrStatus = "created";
        state.closureOriginalData = payload.data.data;
        state.closureMsgData = normalizeMessages(payload.data.data);
        state.isCMPending = false;
        state.isCMSave = true;
        state.isCMDelete = false;
        state.isCMError = false;
      })
      .addCase(updateRepeatOccureneceClosureMsg.rejected, (state) => {
        state.closureMsgUpdateRepeatOccrStatus = "rejected";
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = true;
      })
      .addCase(getResetClosureMsg, (state) => {
        state.closureMsgStatus = "idle";
        state.closureMsgDeleteStatus = "idle";
        state.closureMsgUpdateRepeatOccrStatus = "idle";
        state.isCMPending = false;
        state.isCMSave = false;
        state.isCMDelete = false;
        state.isCMError = false;
      });
  },
});

export const { updateCMData, createCMData, deleteCMData, resetHighlight } =
  closureMsgSlice.actions;
export default closureMsgSlice.reducer;
